import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getManagedSlots, saveAvailabilityDay } from '../api/client';
import { MonthGrid, startOfToday } from './SlotPicker';
import { PRESET_SLOT_TIMES, formatSlotTime } from '../utils/serviceApplications';
import useLiveRefresh from '../hooks/useLiveRefresh';

const TYPES = [
  { id: 'testdrive', label: 'Test drive' },
  { id: 'inspection', label: 'Inspection' },
  { id: 'service', label: 'Service & maintenance' },
];

const AvailabilityManager = () => {
  const [serviceType, setServiceType] = useState('testdrive');
  const [slots, setSlots] = useState([]);
  const [cursor, setCursor] = useState(() => startOfToday());
  const [selected, setSelected] = useState('');
  const [times, setTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  // true once the admin changes the open day's times; a background refresh then leaves them alone
  const [dirty, setDirty] = useState(false);
  const view = useRef({ serviceType, selected, dirty });
  view.current = { serviceType, selected, dirty };

  const load = async (type) => {
    setLoading(true);
    setMessage('');
    try {
      setSlots(await getManagedSlots(type));
    } catch (err) {
      setMessage(err?.response?.data?.detail || 'Could not load availability.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(serviceType);
    setSelected('');
    setTimes([]);
    setDirty(false);
  }, [serviceType]);

  // New bookings (and days opened elsewhere, e.g. in the ops hub) show up without a manual refresh.
  useLiveRefresh(async () => {
    const type = view.current.serviceType;
    const fresh = await getManagedSlots(type);
    if (view.current.serviceType !== type) return;
    setSlots(fresh);
    const day = view.current.selected;
    if (day && !view.current.dirty) {
      setTimes(fresh.filter((slot) => slot.slot_date === day).map((slot) => slot.slot_time));
    }
  }, { enabled: !saving });

  const byDate = useMemo(() => {
    const map = new Map();
    slots.forEach((slot) => {
      const list = map.get(slot.slot_date) || [];
      list.push(slot);
      map.set(slot.slot_date, list);
    });
    return map;
  }, [slots]);

  const booked = new Set((byDate.get(selected) || []).filter((slot) => slot.booked).map((slot) => slot.slot_time));

  const chooseDate = (key) => {
    setSelected(key);
    setTimes((byDate.get(key) || []).map((slot) => slot.slot_time));
    setDirty(false);
    setMessage('');
  };

  const toggleTime = (slot) => {
    if (booked.has(slot)) return;
    setTimes((prev) => (prev.includes(slot) ? prev.filter((item) => item !== slot) : [...prev, slot]));
    setDirty(true);
  };

  const selectAll = () => {
    setTimes((prev) => Array.from(new Set([...prev, ...PRESET_SLOT_TIMES])));
    setDirty(true);
  };

  // A booked time stays open: the customer already has that appointment.
  const clearAll = () => {
    setTimes((prev) => prev.filter((slot) => booked.has(slot)));
    setDirty(true);
  };

  const allSelected = PRESET_SLOT_TIMES.every((slot) => times.includes(slot));
  const nothingToClear = times.every((slot) => booked.has(slot));

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setMessage('');
    try {
      const next = await saveAvailabilityDay({
        service_type: serviceType,
        slot_date: selected,
        times,
      });
      setSlots(next);
      setDirty(false);
      setMessage('Availability saved.');
    } catch (err) {
      setMessage(err?.response?.data?.detail || 'Could not save this day.');
    } finally {
      setSaving(false);
    }
  };

  const today = startOfToday();

  return (
    <section className="dash-panel availability-panel">
      <div className="dash-panel-head">
        <h2>
          <i className="fas fa-calendar-days" /> Appointment availability
        </h2>
      </div>
      <div className="availability-body">
        <div className="availability-types">
          {TYPES.map((type) => (
            <button
              key={type.id}
              type="button"
              className={serviceType === type.id ? 'active' : ''}
              onClick={() => setServiceType(type.id)}
            >
              {type.label}
            </button>
          ))}
        </div>
        <p className="form-help">
          Open future days and times for this service. A time disappears for other customers once someone books it.
        </p>
        {loading ? <p>Loading days…</p> : null}
        <MonthGrid
          cursor={cursor}
          onCursor={setCursor}
          selected={selected}
          onSelect={chooseDate}
          isEnabled={(date) => date >= today}
          hasMark={(key) => byDate.has(key)}
        />
        {selected ? (
          <div className="availability-times">
            <strong>{selected}</strong>
            <div className="slot-times">
              {PRESET_SLOT_TIMES.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  className={`${times.includes(slot) ? 'active' : ''}${booked.has(slot) ? ' booked' : ''}`}
                  onClick={() => toggleTime(slot)}
                  disabled={booked.has(slot)}
                >
                  {formatSlotTime(slot)}
                  {booked.has(slot) ? ' · Booked' : ''}
                </button>
              ))}
            </div>
            <div className="availability-bulk-actions">
              <button type="button" onClick={selectAll} disabled={allSelected}>
                <i className="fas fa-check-double" /> Select All
              </button>
              <button
                type="button"
                onClick={clearAll}
                disabled={nothingToClear}
                title={booked.size ? 'Booked times stay open' : undefined}
              >
                <i className="fas fa-eraser" /> Clear All
              </button>
            </div>
            <button type="button" className="btn-submit" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save this day'}
            </button>
          </div>
        ) : (
          <p className="form-help">Select a future day, then choose the times customers can book.</p>
        )}
        {message ? <p className="form-help">{message}</p> : null}
      </div>
    </section>
  );
};

export default AvailabilityManager;
